/** SSOT for concurrency caps. Hard rule: caps default to the typical dev-host envelope -- never remove a cap to satisfy
 * an outlier oversize-host report. Paired forbid-rules: `custom/no-literal-mapinbatches-concurrency-5`, `-10`,
 * `custom/no-file-local-concurrency-const`. */

/** Default bounded-concurrency cap for `mapInBatches` fan-outs with no context-specific tuning, sized for the
 * conservative D1-read / KV-read / outbound-HTTP workload. For a heavier budget use `DEFAULT_PARALLEL_BATCH_SIZE` (10)
 * from `const/config-pagination.ts`, or a per-feature cap. */
export const MAP_IN_BATCHES_DEFAULT_CONCURRENCY = 5;

/** SEQUENTIAL fan-out: a `mapInBatches` window of one, so each mapper completes before the next starts. The value IS
 * the ordering guarantee -- a money walk whose per-record contract is "one at a time, a failure captured per record"
 * declares its runtime arity through the batched helper while keeping that contract, so this must never be raised to
 * "tune" a caller. Consumers: `returnDepositsForOffers` (`flows/mm-fall-through.ts`), whose per-offer deposit-return
 * runs are sequential by design. */
export const SEQUENTIAL_FANOUT_CONCURRENCY = 1;

/** KV fan-out reads against the noor workspace namespace (one workspace). */
export const NOOR_KV_FANOUT_CONCURRENCY = 10;

/** Max concurrent eslint child processes per shard run -- bounds peak memory across shards.
 *
 * TWO, DOWN FROM FOUR (2026-09-22, iss-7f4346af), measured beside the local daemon's measuring rows. Four shards at
 * normal priority (02:09:58Z, 1,479 s) put every measuring row past the minute for the whole sweep; four at
 * below-normal priority (run-1790048674300-f2ffe7706f40, 1,133 s cold) still put the lane tick at 92,446 ms against a
 * quiet maximum of 42,879 and the rule tick and flow-registration-check over the minute -- while the sweep itself lost
 * nothing to the priority. A scheduler that yields the cores cures nothing when the contention is memory and disk, and
 * two of the four shards held 5,211 MB and 1,982 MB working sets. The lever that reaches memory is how many shards are
 * alive at once. The priority stays (`lowerChildPriority` at every spawn); the per-child deadline scales with this
 * width (`eslintChildDeadlineMs`), so at two it is 600 s under its 1,200 s ceiling. */
export const ESLINT_SHARD_CONCURRENCY = 2;

/** Concurrent `items.delete` calls inside ONE `code-catalog-sync` delete batch: HALF the lane's upload pool
 * (`CODE_CATALOG_SYNC.UPLOAD_CONCURRENCY`). Measured 2026-09-06 (iss-c65ef336): an 8-wide delete pool drew
 * `AiSearchError: You are being rate limited.` on most rows of every 500-row chunk, a throttled row read as a failure,
 * the failure pinned the driver's cursor, and the pinned cursor re-sent the whole ~3,300-row delete set into the same
 * throttle on the next firing. The chunk ceiling derives from this width (`CODE_CATALOG_SYNC.DELETE_BATCH_MAX_ROWS`). */
export const CODE_CATALOG_DELETE_CONCURRENCY = 4;

/** Max concurrent esbuild bundle builds in the hook-artifact builder. Each target is an independent
 * temp-then-rename transaction over a distinct outFile, so they are safe to overlap -- but each spawns its own
 * esbuild pipeline, so the cap bounds peak memory the way `ESLINT_SHARD_CONCURRENCY` does for lint children. */
export const HOOK_ARTIFACT_BUILD_CONCURRENCY = 4;

// NO FlowRunner session run cap: a cap sized against contention on ONE shared wrangler platform proxy describes a
// mechanism the HTTP dev stack does not have. A cap for the dev stack is a NEW measurement against ITS mechanism,
// authored here when that stack declares one.

/** Concurrent `issue-create` dispatches inside ONE census filing (`fileCensusIssues`, `utils/issues/census-issue-filer.ts`).
 * Measured 2026-09-09 (iss-03c8f98c): one filing costs ~15 s (build, duplicate probe, leaf write through the record
 * door, D1 insert), the censuses' file step walls at 120 s, and eight breached rows filed in SERIES died at the wall
 * after the reading was answered -- a false red on the standard for standards' own detector. Four wide keeps eight rows
 * inside one wall and the D1 duplicate probe unthrottled; receipts keep the composed order. */
export const CENSUS_FILING_CONCURRENCY = 4;
